import { Component, EventEmitter, Output, ViewChild, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClientAccessStatusResponse } from '@interfaces/partners/responses/client-access.response';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { ClientAccessHttpService } from '@services/http/partners/client-access-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { describeClientError } from '@src/app/shared/utils/client-error-message';

/**
 * Modal "Acesso do cliente: <empresa>" (card 3461; protótipo `v2/core.jsx`, ConviteClienteCard).
 *
 * Cria o usuário da empresa do deal (papel COMPANY) com nome e e-mail; o backend manda o e-mail de
 * criar senha e registra o convite na linha do tempo do deal. A seção "O que o link inclui" do
 * protótipo não entra (decisão de 16/10/2026): o link sempre leva à área do cliente.
 *
 * ⚠️ O botão do rodapé é `type="button"` com `(sendButtonClick)`: `type="submit"` dentro de form
 * junto com `(sendButtonClick)` chama o handler duas vezes (bug conhecido do `app-button`).
 */
@Component({
  selector: 'app-modal-client-access',
  standalone: true,
  templateUrl: './modal-client-access.component.html',
  imports: [ReactiveFormsModule, SharedModule],
})
export class ModalClientAccessComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** Devolve ao card a situação já atualizada que o backend respondeu. */
  @Output() invited = new EventEmitter<ClientAccessStatusResponse | null>();

  /**
   * Enviando. Nome próprio em vez de `isLoading` de `VaultBaseComponent`: aquele é o spinner global
   * da aplicação (ver `modal-partner-user`).
   */
  readonly saving = signal(false);

  readonly title = signal('Acesso do cliente');

  private dealId: string | null = null;

  form: FormGroup = this.formBuilder.group({
    name: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
  });

  constructor(
    translateService: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly clientAccessService: ClientAccessHttpService,
    private readonly toast: ToastHelperService
  ) {
    super(translateService);
  }

  /** Abre a modal limpa para o deal informado. */
  open(dealId: string, companyName: string | null | undefined): void {
    this.dealId = dealId;
    this.title.set(companyName ? `Acesso do cliente: ${companyName}` : 'Acesso do cliente');
    this.form.reset({ name: '', email: '' });
    this.saving.set(false);
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'client-access', false, true);
  }

  /** Nome preenchido e e-mail válido (espaços nas pontas não contam). */
  isFormValid(): boolean {
    const name = (this.form.get('name')?.value ?? '').trim();
    return !!name && this.form.valid;
  }

  submitAction(): void {
    if (!this.dealId || !this.isFormValid() || this.saving()) return;

    const email = (this.form.get('email')?.value ?? '').trim();
    const request = { name: (this.form.get('name')?.value ?? '').trim(), email };

    this.saving.set(true);
    this.clientAccessService.invite(this.dealId, request).subscribe({
      next: status => {
        this.saving.set(false);
        this.toast.showToastSuccess(
          'Acesso criado',
          `Acesso criado e link enviado para ${email}. O cliente assina o NDA + carta SCR pela área dele. O status aparece aqui.`
        );
        this.dialogRef?.close();
        this.invited.emit(status);
      },
      error: error => {
        this.saving.set(false);
        // A mensagem do backend diz o que fazer ("este e-mail já tem acesso..."); texto genérico só
        // para falha técnica.
        this.toast.showToastError(
          'Não foi possível criar o acesso',
          describeClientError(
            error,
            'Confira a sua conexão e clique em Enviar convite de novo; se continuar assim, fale com o time da Legatus.'
          )
        );
      },
    });
  }
}
